import isEqual from 'lodash/isEqual'
import uniq from 'lodash/uniq'

import { NO_COLUMNS } from '../constants/noColumns'

interface GetHiddenColumnsStateOptions {
  // Ids of every column the table has right now.
  columnsIds: string[]
  // Ids of the columns the config marks as hidden right now.
  defaultHiddenColumnsIds: string[]
  // Ids of the default-hidden columns flagged as new. Only used to migrate storage saved before known columns existed.
  newColumnsIds?: string[]
  // `null` or missing when nothing was saved yet.
  storedDefaultHiddenColumns?: string[] | null
  storedHiddenColumns?: string[] | null
  storedKnownColumns?: string[] | null
}

interface HiddenColumnsState {
  // Columns that start hidden and that the user has not switched on yet.
  defaultHiddenColumns: string[]
  // Every column to hide: the ones the user hid and the default-hidden ones.
  hiddenColumns: string[]
  // True when the saved default-hidden and known columns already match, so nothing has to be written.
  isStorageUpToDate: boolean
  // Default-hidden columns the user has already been offered.
  knownColumns: string[]
}

/*
 * Works out which columns of a table are hidden, from what is saved for the table and from the table's columns now.
 * `useHiddenColumns` saves the result.
 *
 * Once known columns are stored, a default-hidden column that is not known yet starts hidden. Storage saved before
 * known columns existed has no such list, so every default-hidden column counts as known there, except the ones in
 * `newColumnsIds`, because a default-hidden column the user switched on is missing from both older keys. Without any
 * storage all default-hidden columns start hidden.
 *
 * A column that the config no longer marks as hidden is dropped from the default hidden columns, so it becomes
 * visible. Only columns that are part of the table right now (`columnsIds`) are dropped. A saved default-hidden column
 * that is absent from the table (for example one offered only under some filters) stays saved, so it is still hidden
 * when it is offered again.
 *
 * A column that is part of the table but no longer default-hidden is also forgotten as known. If the config hides it
 * again later, it counts as new and starts hidden again. A known column that is absent from the table stays known, and
 * a default-hidden column that is absent is not marked known until it is offered.
 * */
export const getHiddenColumnsState = (options: GetHiddenColumnsStateOptions): HiddenColumnsState => {
  const { columnsIds, defaultHiddenColumnsIds, storedKnownColumns } = options
  const newColumnsIds = options.newColumnsIds ?? NO_COLUMNS
  const storedDefaultHiddenColumns = options.storedDefaultHiddenColumns ?? NO_COLUMNS
  const storedHiddenColumns = options.storedHiddenColumns ?? NO_COLUMNS
  const hasLegacyStorage = options.storedHiddenColumns != null || options.storedDefaultHiddenColumns != null
  // Without the known columns saved (older storage), the ids in the hidden and default hidden columns are known. A
  // default-hidden column missing from both was switched on by the user, unless it is flagged as new.
  const legacyKnownColumns = hasLegacyStorage
    ? [
        ...storedHiddenColumns,
        ...storedDefaultHiddenColumns,
        ...defaultHiddenColumnsIds.filter((columnId) => !newColumnsIds.includes(columnId)),
      ]
    : NO_COLUMNS
  const previouslyKnownColumns = storedKnownColumns ?? legacyKnownColumns

  const newDefaultHiddenColumns = defaultHiddenColumnsIds.filter(
    (columnId) => !previouslyKnownColumns.includes(columnId),
  )

  // A saved column stays default-hidden while the config hides it, or while the table has no such column right now.
  const stillDefaultHiddenColumns = storedDefaultHiddenColumns.filter(
    (columnId) => defaultHiddenColumnsIds.includes(columnId) || !columnsIds.includes(columnId),
  )
  const defaultHiddenColumns = uniq([...stillDefaultHiddenColumns, ...newDefaultHiddenColumns])

  // A column that is part of the table but no longer default-hidden is forgotten. If the config hides it again later,
  // it counts as new and starts hidden again.
  const stillKnownColumns = previouslyKnownColumns.filter(
    (columnId) => defaultHiddenColumnsIds.includes(columnId) || !columnsIds.includes(columnId),
  )
  const knownColumns = uniq([...stillKnownColumns, ...defaultHiddenColumnsIds])

  return {
    defaultHiddenColumns,
    hiddenColumns: uniq([...storedHiddenColumns, ...defaultHiddenColumns]),
    isStorageUpToDate:
      isEqual(storedDefaultHiddenColumns, defaultHiddenColumns) &&
      isEqual(storedKnownColumns ?? NO_COLUMNS, knownColumns),
    knownColumns,
  }
}
